import React, { useState, useEffect } from 'react'
import { Modal, Button, Input, Checkbox, Spin, message } from 'antd'
import { useTranslation, Trans } from 'react-i18next'
import { api } from '../../../../services'
import OrganizationPicker from './OrganizationPicker'
import GroupPicker from './GroupPicker'
import PrivacySelector from './PrivacySelector'
import { general } from '../../../../constants'
import _ from 'lodash'

const PublishQuestModal = ({ visible, onClose, hunt, user, onPublishSuccess, orgList }) => {
    const { t } = useTranslation()
    const { active_organization_id, id: user_id, isAdmin, isSuperAdmin } = user?.info || {}

    const [loading, setLoading] = useState(false)
    const [selectedType, setSelectedType] = useState(2)
    const [selectedOrganizations, setSelectedOrganizations] = useState([])
    const [selectedGroups, setSelectedGroups] = useState([])
    const [emails, setEmails] = useState([])
    const [isEmailsValid, setIsEmailsValid] = useState(false)
    const [isAdminOnly, setIsAdminOnly] = useState(false)
    const [isSendMail, setIsSendMail] = useState(false)
    const [checkedTerms, setCheckedTerms] = useState(false)
    const [isDisable, setIsDisable] = useState(true)
    useEffect(() => {
        if (visible && hunt && hunt.setting) {
            const {
                group_ids,
                publish_option,
                organization_ids,
                emails: existingEmails,
                is_admin_only
            } = hunt.setting

            if (publish_option === general.ORGANIZATION) {
                setSelectedType(2)
            } else if (publish_option === general.ORGANIZATION_GROUP) {
                setSelectedType(3)
            } else if (publish_option === general.SPECIFIC_USER) {
                setSelectedType(4)
            } else if (publish_option === general.PUBLIC) {
                setSelectedType(1)
            }

            setSelectedOrganizations(
                (hunt.published_orgs || []).map(el => typeof el.target_org_id === 'string' ? parseInt(el.target_org_id, 10) : el.target_org_id)
            )
            setSelectedGroups(group_ids || [])
            setEmails(existingEmails || [])
            setIsAdminOnly(is_admin_only || false)

            setCheckedTerms(false)
            setIsDisable(true)
        } else if (visible) {
            setSelectedType(2)
            setSelectedOrganizations((hunt?.published_orgs || []).map(el => typeof el.target_org_id === 'string' ? parseInt(el.target_org_id, 10) : el.target_org_id))
            setSelectedGroups([])
            setEmails([])
            setIsAdminOnly(false)
            setCheckedTerms(false)
            setIsDisable(true)
        }
    }, [visible, hunt])

    const privacyTypes = [
        isSuperAdmin && {
            id: 1,
            name: 'Public',
            translationKey: 'public',
            disabled: true
        },
        (isSuperAdmin || isAdmin) && {
            id: 2,
            name: 'Organizations',
            translationKey: 'organisations'
        },
        (isSuperAdmin || isAdmin) && {
            id: 3,
            name: 'Org Groups',
            translationKey: 'org_groups',
            disabled: true
        },
        {
            id: 4,
            name: 'Specific User',
            translationKey: 'specific_user',
            disabled: true
        }
    ].filter(i => !!i)

    const handleInsertUsers = (value) => {
        const emailList = value.replace(/(\r\n|\n|\r)/gm, ',').split(',')
        const check = _.map(_.compact(emailList), email =>
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email.trim())
        )
        const isSetValid = _.filter(check, item => item)
        const allEmails = value.trim().split('\n').filter(i => !!i)

        setEmails(allEmails)
        setIsEmailsValid(isSetValid.length === check.length)
    }

    const getLastOption = () => {
        if (selectedType === 1) return general.PUBLIC
        if (selectedType === 2) return general.ORGANIZATION
        if (selectedType === 3) return general.ORGANIZATION_GROUP
        return general.SPECIFIC_USER
    }

    const handlePublish = async () => {
        if (selectedType > 0 && !checkedTerms) {
            message.error(t('quest:please_agree_terms'))
            return
        }

        if (selectedType === 4 && !isEmailsValid && emails.length > 0) {
            message.error(t('quest:emails_validation_error'))
            return
        }

        setLoading(true)
        try {
            const removedSpaceEmail = _.map(emails, email => email.trim().toLowerCase())

            // const privacy = {
            //     is_public: selectedType === 1,
            //     is_admin_only: selectedType === 1 ? isAdminOnly : false,
            //     organization_ids: selectedOrganizations,
            //     group_ids: selectedGroups,
            //     emails: _.uniq([...removedSpaceEmail]),
            //     publish_option: getLastOption()
            // }

            // const payload = {
            //     setting: privacy,
            //     isSendMail
            // }
            const payload = {
                user_id: String(user_id),
                org_ids: selectedOrganizations.map(String)
            }

            const res = await api.quest.publishQuest(hunt.hunt_id, payload)

            if (res && res.status === 200) {
                message.success(t('quest:publish_success'))
                if (onPublishSuccess) onPublishSuccess()
                onClose()
            } else {
                message.error(t('quest:publish_error'))
            }
        } catch (error) {
            message.error(t('quest:publish_error'))
        } finally {
            setLoading(false)
        }
    }

    const handleSelectedOrganizations = async (id) => {
        if (selectedOrganizations.includes(id)) {
            // Unselecting
            const wasPublished = (hunt.published_orgs || []).map(el => typeof el.target_org_id === 'string' ? parseInt(el.target_org_id, 10) : el.target_org_id).includes(id);
            if (wasPublished) {
                try {
                    const res = await api.quest.deletePublishQuest(hunt.hunt_id, id, user_id);
                    if (res && res.status === 200) {
                        setSelectedOrganizations(prev => prev.filter(orgId => orgId !== id));
                        if (onPublishSuccess) onPublishSuccess();
                    }
                } catch (error) {
                    console.error('Error unpublishing quest:', error);
                }
            } else {
                setSelectedOrganizations(prev => prev.filter(orgId => orgId !== id));
            }
        } else {
            // Selecting
            setSelectedOrganizations(prev => [...prev, id]);
        }
    }

    const handleSelectedGroups = (groupId) => {
        setSelectedGroups(prev =>
            prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
        )
    }

    return (
        <Modal
            title={t('publishsettings:title').toUpperCase()}
            visible={visible}
            onCancel={onClose}
            width={'50%'}
            footer={[
                <Button key="back" onClick={onClose} className="rounded">
                    {t('buttons:cancel')}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handlePublish}
                    disabled={selectedType > 0 && isDisable}
                    className="rounded"
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                >
                    {t('quest:publish_quest')}
                </Button>
            ]}
        >
            <Spin spinning={loading}>
                <div style={{ marginBottom: 24 }}>
                    <PrivacySelector
                        selectedType={selectedType}
                        types={privacyTypes}
                        isAdmin={isSuperAdmin || isAdmin}
                        onChange={setSelectedType}
                    />
                </div>

                {selectedType === 2 && (
                    <div style={{ marginBottom: 24 }}>
                        <OrganizationPicker
                            list={orgList}
                            selectedOrganizations={selectedOrganizations}
                            onChange={handleSelectedOrganizations}
                        />
                    </div>
                )}

                {selectedType === 3 && (
                    <div style={{ marginBottom: 24 }}>
                        <GroupPicker
                            list={orgList}
                            selectedGroups={selectedGroups}
                            onChange={handleSelectedGroups}
                        />
                    </div>
                )}

                {selectedType === 4 && (
                    <div style={{ marginBottom: 24 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>{t('quest:users_emails')}</label>
                        <Input.TextArea
                            rows={4}
                            onChange={e => handleInsertUsers(e.target.value)}
                            defaultValue={emails.join('\n')}
                            placeholder="Enter emails, one per line"
                        />
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>{t('quest:description_specifiction_email')}</span>
                    </div>
                )}

                {selectedType > 0 && (
                    <>
                        <div style={{ marginBottom: 24 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>{t('labels:disclaimer')}</label>
                            <Input.TextArea
                                disabled
                                rows={4}
                                style={{ resize: 'none' }}
                                value={t('events:disclaimer')}
                            />
                        </div>
                        <div style={{ marginBottom: 24 }}>
                            <Checkbox
                                checked={checkedTerms}
                                onChange={(e) => {
                                    setCheckedTerms(e.target.checked)
                                    setIsDisable(!e.target.checked)
                                }}
                            >
                                <Trans i18nKey="publishsettings:terms">
                                    {t('publishsettings:i_agree_to_these')} &nbsp;
                                    <a href="/pages/terms-conditions" target="_blank">
                                        {t('publishsettings:terms_and_conditions')}
                                    </a>
                                </Trans>
                            </Checkbox>
                        </div>
                    </>
                )}

                <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                    {selectedType === 1 && (
                        <Checkbox onChange={e => setIsAdminOnly(e.target.checked)} checked={isAdminOnly}>
                            {t('v4:admin_only')}
                        </Checkbox>
                    )}
                    {(selectedType === 2 || selectedType === 3) && (
                        <Checkbox onChange={e => setIsSendMail(e.target.checked)} checked={isSendMail}>
                            {t('v4:send_email')}
                        </Checkbox>
                    )}
                </div>
            </Spin>
        </Modal>
    )
}

export default PublishQuestModal
